import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@Utils';

import { add_svg } from '@Assets';
import { PageWrapper, UserAddModal, UserList } from '@Components';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@Components/ui-v2/combobox';
import { Input } from '@Components/ui-v2/input';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { UserProps } from '@Models';

const PAGE_SIZE = 9;

const User = ({ clients, users, fetchUsers, total }: UserProps) => {
  const { t } = useTranslation();
  const [client, setClient] = useState('');
  const [email, setEmail] = useState('');
  const [usersDisplay, setUsersDisplay] = useState(users);
  const [loading, setloading] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [totalUsers, setTotal] = useState(total);

  useEffect(() => {
    setUsersDisplay(users);
  }, [users]);

  const filterUsers = useMemo(() => {
    return client ? usersDisplay.filter((user) => user['app_metadata.account'] === client) : usersDisplay;
  }, [client, usersDisplay]);

  const clientsObjects = useMemo(() => {
    const object = {};
    clients.forEach((element) => {
      if (!object[element.value]) {
        object[element.value] = element.label;
      }
    });

    return object;
  }, [clients]);

  const clientsSort = useMemo(() => {
    return [...clients].sort((a, b) => a.label.localeCompare(b.label.trim()));
  }, [clients]);

  const handleClient = (item: { value: string; label: string } | null): void => {
    setClient(item?.value ?? '');
  };

  const selectedClient = useMemo(
    () => clientsSort.find((option) => option.value === client) ?? null,
    [client, clientsSort],
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value;
    setEmail(value);
  };

  return (
    <div className={cn(loading && 'pointer-events-none', 'h-full w-full')}>
      <PageWrapper
        title={t('User.Title')}
        actions={
          <TrackedButton buttonName="OPEN_ADD_USER_WINDOW" isSubmit={false} onClick={() => setOpenAdd(true)}>
            <div className="flex">
              <img src={add_svg} alt="add_user" className="w-4 text-white mr-2" />
              {t('User.Add')}
            </div>
          </TrackedButton>
        }>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="flex flex-row mx-6 my-4 items-center">
            <span className="font-bold text-xl justify-start  text-gray-700">
              {' '}
              {`${t('User.All')} (${client ? totalUsers : total})`}
            </span>
            <div className="ml-auto flex gap-2 w-[50%] lg:flex-row flex-col">
              <div className="w-full">
                <Combobox
                  items={clientsSort}
                  value={selectedClient}
                  onValueChange={handleClient}
                  itemToStringLabel={(item) => item.label}>
                  <ComboboxInput placeholder={t('User.Select')} />
                  <ComboboxContent>
                    <ComboboxEmpty>{t('User.Select')}</ComboboxEmpty>
                    <ComboboxList>
                      {(item: { value: string; label: string }) => (
                        <ComboboxItem key={item.value} value={item}>
                          {item.label}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </div>
              <div className="relative w-full sm:w-auto sm:min-w-[260px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                <Input type="text" id="email" onChange={handleSearch} className="pl-9" placeholder={t('User.Search')} />
              </div>
            </div>
          </div>

          <UserList
            usersData={filterUsers}
            clientsObjects={clientsObjects}
            total={total}
            client={client}
            emailQuery={email}
            setTotal={setTotal}
            pageSize={PAGE_SIZE}
            clients={clientsSort}
          />
          <UserAddModal
            isOpen={openAdd}
            onClose={() => setOpenAdd(false)}
            clients={clientsSort}
            setloading={setloading}
            setUsersDisplay={setUsersDisplay}
            fetchUsers={fetchUsers}
            usersDisplay={usersDisplay}
            clientValue={client ? { value: client, label: clientsObjects ? clientsObjects[client] : '' } : undefined}
          />
        </div>
      </PageWrapper>
    </div>
  );
};
export default User;
